import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import { NumberFormat } from 'shared/types/number-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';

import * as Styled from './goal-insights-card.style';
import { Header } from './header';
import { InsightsCardContent } from './insights-card-content';

export type NoMetricCardProps = {
  goal: Goal;
  goals: Goal[];
  lifeAreas: LifeArea[];
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  numberFormat?: NumberFormat;
  onSelectMetric?: () => void;
  onPremium: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
  onMenuButton?: (e: React.MouseEvent) => void;
  hasBigTitle?: boolean;
  isLink?: boolean;
  showLeftAxis?: boolean;
};

export const NoMetricCard: React.FC<NoMetricCardProps> = ({
  goal,
  goals,
  lifeAreas,
  startDate,
  endDate,
  weekStartsOn,
  numberFormat,
  onSelectMetric,
  onPremium,
  onContextMenu,
  menuButtonRef,
  onMenuButton,
  hasBigTitle,
  isLink,
  showLeftAxis,
}) => {
  const { t } = useTranslation();

  const linksToDetail = !(!isLink || goal.frozenAt);

  // The card is a link, so the button must not navigate — same guard the header's own buttons use.
  const handleSelectMetric = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectMetric?.();
  };

  return (
    <Styled.Container
      as={linksToDetail ? undefined : 'span'}
      to={linksToDetail ? getGoalDetailUrl(goal) : ''}
      onClick={goal.frozenAt ? onPremium : undefined}
      onContextMenu={goal.frozenAt ? undefined : onContextMenu}
    >
      <Header
        goal={goal}
        goals={goals}
        lifeAreas={lifeAreas}
        onMenuButton={onMenuButton}
        menuButtonRef={menuButtonRef}
        hasBigTitle={hasBigTitle}
      />

      <InsightsCardContent
        goal={goal}
        entries={[]}
        startDate={startDate}
        endDate={endDate}
        weekStartsOn={weekStartsOn}
        numberFormat={numberFormat}
        isEmpty
        isLink={isLink}
        showLeftAxis={showLeftAxis}
        noMetricAction={
          <Button
            variant={Variants.ContainedLight}
            color={Colors.Primary}
            size={Sizes.Small}
            onClick={handleSelectMetric}
            disabled={!onSelectMetric}
          >
            {t('general.labels.select-metric')}
          </Button>
        }
      />
    </Styled.Container>
  );
};
